import { DealStep, NavOption, StepDetail } from "../types";

export const NDA_DESCRIPTION = `
  This MNDA protects you and any information shared between you and ESO Fund. It also prohibits the sharing of ESO Fund materials with anyone other than the professional advisors assisting you on this transaction.
  <br /><br />
  After signing, please send me the following items, ensuring that both the <strong>company name</strong> and <strong>current date</strong> are visible:
  <br /><br />
  1) <strong>Proof of ownership</strong>:
  <br />
    - If you have options, please share screenshots or grant documents indicating the <strong>vested amounts, grant dates, and strike prices</strong><br />
    - If you have shares, please share a screenshot of your stock certificates
  <br /><br />
  2) <strong>The current FMV (Fair Market Value), aka 409A share price</strong>:
  <br />
    - In Carta, the FMV may be found on either the Portfolio page, or near the bottom Exercise Simulator page<br />
    - In Shareworks, the FMV may be found on the Dashboard<br />
    - Other equity portals will typically display the FMV on a summary page<br />
    - Otherwise, ask your stock plan administrator or CFO for the current 409A value
  <br /><br />
  Once we receive these documents, we will do our best to get you an offer out as soon as possible.
  <br /><br />
  For an overview of our funding process, please go to our <a href='https://esofund.com/overview'>website</a>.
    `

export const NAV_OPTIONS = [
  NavOption.CURRENT_OFFER,
  NavOption.DOCUMENTS,
  NavOption.COMPLIANCE,
]

export const NAV_OPTIONS_NO_DEALS = [
  NavOption.DOCUMENTS,
  NavOption.ACCOUNT,
]

export const DEAL_MANAGER_NAV_OPTIONS = [
  NavOption.DEALS
]
  

export const STATE_ABBREVIATIONS_MAP = {
  "Alabama": "AL",
  "Alaska": "AK",
  "Arizona": "AZ",
  "Arkansas": "AR",
  "California": "CA",
  "Colorado": "CO",
  "Connecticut": "CT",
  "Delaware": "DE",
  "Florida": "FL",
  "Georgia": "GA",
  "Hawaii": "HI",
  "Idaho": "ID",
  "Illinois": "IL",
  "Indiana": "IN",
  "Iowa": "IA",
  "Kansas": "KS",
  "Kentucky": "KY",
  "Louisiana": "LA",
  "Maine": "ME",
  "Maryland": "MD",
  "Massachusetts": "MA",
  "Michigan": "MI",
  "Minnesota": "MN",
  "Mississippi": "MS",
  "Missouri": "MO",
  "Montana": "MT",
  "Nebraska": "NE",
  "Nevada": "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  "Ohio": "OH",
  "Oklahoma": "OK",
  "Oregon": "OR",
  "Pennsylvania": "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  "Tennessee": "TN",
  "Texas": "TX",
  "Utah": "UT",
  "Vermont": "VT",
  "Virginia": "VA",
  "Washington": "WA",
  "West Virginia": "WV",
  "Wisconsin": "WI",
  "Wyoming": "WY"
}

export const STATES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming"
]

const ndaDescription = `Navigate to the "Compliance" section and select the NDA tab. Fill out the form and submit it.`
const signNdaDescription = `Once you have submitted the NDA form, you will receive an email from DocuSign to electronically sign the NDA. Please check your spam folder if you do not see it in your inbox.` 
const uploadGrantDocumentsDescription = `After signing the NDA, you can upload your grant documents in the "Documents" section. Please ensure that the documents clearly show your vested amounts, grant dates, and strike prices.`
const acceptOfferDescription = `Once your offer is ready, you will receive an email notification. You can then review and accept the offer in the "Your Deals" section.`
const completeBackgroundCheckFormDescription = `Navigate to the <a href="/compliance" style="color:#3F5EA1;text-decoration:underline;">Compliance</a> section and select the Background Check tab. Fill out the form and submit it.`
const signBackgroundCheckDescription = `After submitting the Background Check form, you will receive an email from DocuSign to electronically sign the Background Check authorization. Please check your spam folder if you do not see it in your inbox.`
const uploadClosingDocsDescription = `Upload your closing documents in the "Documents" section. Please ensure that you upload all required documents to avoid delays in closing.`
const scheduleClosingMeetingDescription = `After the Background Credit Check is approved, you will receive an email from DocuSign with the contract as well as a separate email with our Limited Power of Attorney and a link to schedule your closing meeting. Please sign the contract in DocuSign, notarize the LPOA, and choose a time that works best for you. After that, please upload your bank info in our portal.`
const closedDescription = `Congratulations! Your deal is now closed.`

export const getClosingMeetingDescription = (calendlyUrl?: string) => {
  // Sanitize calendly URL: only allow https:// URLs to prevent XSS via javascript: or data: URIs
  const safeUrl = calendlyUrl && /^https:\/\//i.test(calendlyUrl) ? calendlyUrl.replace(/"/g, '&quot;') : null
  const scheduleLink = safeUrl
    ? `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" style="color:#3F5EA1;text-decoration:underline;">schedule your closing meeting</a>`
    : "schedule your closing meeting"
  return `After the Background Credit Check is approved, you will receive an email from DocuSign with the contract as well as a separate email with our Limited Power of Attorney and a link to ${scheduleLink}. Please sign the contract in DocuSign, notarize the LPOA, and choose a time that works best for you. After that, please <a href="/payment" style="color:#3F5EA1;text-decoration:underline;">upload your bank info</a> in our portal.`
}

export const COMPLETE_NDA_FORM_STEP_INDEX = 0;
export const SIGN_NDA_STEP_INDEX = 1;
export const BC_FORM_STEP_INDEX = 4;
export const SIGN_BC_STEP_INDEX = 5;
export const UPLOAD_CLOSING_DOCS_STEP_INDEX = 6;
export const SCHEDULE_CLOSING_MEETING_STEP_INDEX = 7;
export const SIGN_CONTRACT_STEP_INDEX = 8;

export const ALL_STEPS: StepDetail[] = [
  { step: DealStep.COMPLETE_NDA_FORM, title: "Complete NDA Form on Portal", description: ndaDescription, completed: false, isActive: false },
  { step: DealStep.SIGN_NDA, title: "Sign NDA on DocuSign", description: signNdaDescription, completed: false, isActive: false },
  { step: DealStep.UPLOAD_GRANT_DOCS, title: "Upload Grant Documents", description: uploadGrantDocumentsDescription, completed: false, isActive: false },
  { step: DealStep.ACCEPT_OFFER, title: "Review Offer", description: acceptOfferDescription, completed: false, isActive: false },
  { step: DealStep.COMPLETE_BC_FORM, title: "Complete Background Check Form on Portal", description: completeBackgroundCheckFormDescription, completed: false, isActive: false },
  { step: DealStep.SIGN_BC, title: "Sign Background Check on DocuSign", description: signBackgroundCheckDescription, completed: false, isActive: false },
  { step: DealStep.UPLOAD_CLOSING_DOCS, title: "Upload Closing Documents", description: uploadClosingDocsDescription, completed: false, isActive: false },
  { step: DealStep.CLOSING, title: "Closing", description: scheduleClosingMeetingDescription, completed: false, isActive: false },
  { step: DealStep.CLOSED, title: "Closed", description: closedDescription, completed: false, isActive: false },
]

export const BACKGROUND_CHECK_STEPS = [
  { title: "Upload Grant Documents", description: uploadGrantDocumentsDescription, completed: true, isActive: false },
  { title: "Review Offer", description: acceptOfferDescription, completed: true, isActive: false },
  { title: "Complete Background Check Form", description: completeBackgroundCheckFormDescription, completed: false, isActive: true }
]